import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Mail, Building2, User, Clock, Briefcase, 
  RefreshCw, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/contact`);
      setInquiries(response.data);
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setError('Failed to load contact inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'Need help ASAP':
        return 'bg-red-100 text-red-800';
      case 'Looking to start in 1-3 months':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-page">
      <Helmet>
        <title>Admin Dashboard - Cynergy Services</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="section-padding bg-secondary/30 min-h-screen" data-testid="admin-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-sm font-medium uppercase tracking-widest text-primary mb-2">
                  Admin
                </p>
                <h1 className="heading-2 text-foreground">
                  Contact Inquiries
                </h1>
              </div>
              <Button 
                onClick={fetchInquiries} 
                variant="outline" 
                disabled={loading}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-sm mb-6" data-testid="error-message">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : inquiries.length === 0 ? (
              <Card className="text-center py-12" data-testid="no-inquiries">
                <CardContent>
                  <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No contact inquiries yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {inquiries.map((inquiry, index) => (
                  <motion.div
                    key={inquiry.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Card className="h-full" data-testid={`inquiry-card-${index}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-4">
                          <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {inquiry.name}
                          </CardTitle>
                          <Badge className={getUrgencyColor(inquiry.urgency)}>
                            {inquiry.urgency}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-primary" />
                          <a href={`mailto:${inquiry.email}`} className="text-muted-foreground hover:text-primary">
                            {inquiry.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="w-4 h-4 text-primary" />
                          <span className="text-muted-foreground">{inquiry.company}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-primary" />
                          <span className="text-muted-foreground">{inquiry.role}</span>
                        </div>
                        
                        <div>
                          <p className="text-xs font-medium text-primary mb-2">Interests</p>
                          <div className="flex flex-wrap gap-1">
                            {inquiry.interests.map((interest, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {interest}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-xs font-medium text-primary mb-2">Challenge</p>
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {inquiry.challenge}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                          <Clock className="w-3 h-3" />
                          {formatDate(inquiry.created_at)}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
